import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  ApDocument,
  ApDocumentInsert,
  ApDocumentSearchCommand,
  ApDocumentSummary,
  ApDocumentType,
  ApNoteCause,
  ApNoteReason,
  PayableDocument,
  PurchaseReceiptNextNumber,
} from '../models/ap-document/ap-document';
import { Currency } from '../models/bank-account/bank-account';
import { PagedResult } from './bank-account.proxy.service';

/**
 * #4842 — the API's classification → service rule, so the form can preview whether a Constancia de Retención
 * will be required. `treatAllAsService` must be honoured: an empty `serviceClassificationCodes` means EVERY
 * classification is a service (the safe default), not that none of them are.
 */
export interface WithholdingClassificationRules {
  serviceClassificationCodes: string[];
  treatAllAsService: boolean;
}

/** #4724 — per-PO-item invoiced-vs-ordered rollup for a Purchase Order (Finance.API PoItemInvoicedDto). */
export interface PoItemInvoiced {
  purchaseOrderItemReference: string;
  itemCode?: string | null;
  description?: string | null;
  orderedQuantity: number;
  orderedAmount: number;
  /** Quantity already invoiced on the counted AP documents (excludes the document being edited). */
  invoicedQuantity: number;
  invoicedAmount: number;
  /** orderedQuantity − invoicedQuantity (can be negative if already over-invoiced). */
  remainingQuantity: number;
}

/**
 * #4783 — result of GET /api/v1/ap/documents/invoice-age. Non-blocking advisory: a vendor invoice issued
 * more than the company's threshold ago (default 60 days) is flagged, but never prevented from being
 * registered. Separate from the #4714 posting-date deviation — different date, different threshold.
 */
export interface InvoiceAge {
  documentDate: string;
  systemDate: string;
  /** How many days old the invoice is; 0 when the issue date is today or in the future. */
  ageDays: number;
  thresholdDays: number;
  exceedsThreshold: boolean;
  message?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ApDocumentProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/ap/documents';

  public getById(id: string): Observable<ApiResponse<ApDocument>> {
    return this.httpClient.get<ApiResponse<ApDocument>>(`${this.baseUrl}/${id}`);
  }

  /** #4783 — non-blocking check of how old the vendor invoice's issue date is. */
  public checkInvoiceAge(companyReference: string, documentDate: string): Observable<ApiResponse<InvoiceAge>> {
    const params = new HttpParams().set('companyReference', companyReference).set('documentDate', documentDate);
    return this.httpClient.get<ApiResponse<InvoiceAge>>(`${this.baseUrl}/invoice-age`, { params });
  }

  public createDraft(data: ApDocumentInsert): Observable<ApiResponse<ApDocument>> {
    return this.httpClient.post<ApiResponse<ApDocument>>(this.baseUrl, data);
  }

  public updateDraft(id: string, data: ApDocumentInsert): Observable<ApiResponse<ApDocument>> {
    return this.httpClient.put<ApiResponse<ApDocument>>(`${this.baseUrl}/${id}`, data);
  }

  public post(id: string): Observable<ApiResponse<ApDocument>> {
    return this.httpClient.post<ApiResponse<ApDocument>>(`${this.baseUrl}/${id}/post`, {});
  }

  /**
   * Cancels a POSTED document: the API reverses its journal entry, marks it Cancelled and takes its
   * invoiced amount back off the purchase orders.
   *
   * <p>Terminal, unlike the AP payment's unpost — posting may have drawn a self-issued CAI correlativo, and
   * an issued fiscal number is not handed back, so the correction is a NEW document. Restricted to the
   * FinanceTransactionReversal support role, and refused while a payment, an advance, a Constancia, a note
   * or an open payment request still stands against it.</p>
   */
  public cancel(id: string, reason?: string): Observable<ApiResponse<ApDocument>> {
    return this.httpClient.post<ApiResponse<ApDocument>>(`${this.baseUrl}/${id}/cancel`, { reason: reason ?? null });
  }

  /**
   * Waives the document's Constancia de Retención requirement (#4842). Draft-only and one-way — the derived
   * value already errs toward requiring a certificate, so the only override is the one that drops it, and the
   * API rejects a blank reason.
   */
  /**
   * Asserts whether this document needs a Constancia, in either direction (#4842/#5166).
   *
   * One call because there is one requirement flag; the direction decides whether the reason is recorded as
   * a waiver or as a forced requirement. The reason is mandatory both ways — either way a human overruled
   * what the line classifications derived.
   *
   * Removing a requirement is refused once any certificate cites the document; the UI blocks it first so the
   * clerk gets an explanation rather than a rejected request.
   */
  public changeWithholdingRequirement(id: string, requiresWithholding: boolean, reason: string): Observable<ApiResponse<ApDocument>> {
    return this.httpClient.patch<ApiResponse<ApDocument>>(`${this.baseUrl}/${id}/withholding-change`, { requiresWithholding, reason });
  }

  /**
   * Files a supporting document against an AP document. Allowed in any status — an attachment is evidence
   * about the document, not accounting data, and the scan usually arrives after posting.
   *
   * No Content-Type is set: the browser has to add the multipart boundary itself, and setting the header
   * by hand omits it and makes the request unparseable server-side.
   */
  public uploadAttachment(id: string, file: File): Observable<ApiResponse<ApDocument>> {
    const form = new FormData();
    form.append('file', file);
    return this.httpClient.patch<ApiResponse<ApDocument>>(`${this.baseUrl}/${id}/attachments`, form);
  }

  /** Detaches a file by its S3 key. The key travels as a query parameter because it contains slashes. */
  public removeAttachment(id: string, key: string): Observable<ApiResponse<ApDocument>> {
    const params = new HttpParams().set('key', key);
    return this.httpClient.patch<ApiResponse<ApDocument>>(`${this.baseUrl}/${id}/attachments/remove`, null, { params });
  }

  /**
   * The days anything was received against these Purchase Orders (#5165) — the choices offered when dating
   * an Invoice. Distinct days, newest first; three GRNs booked the same day are one receiving event and come
   * back as one option. An empty array is a legitimate answer: nothing is receipted yet, and the clerk keys
   * a date by hand instead of being blocked.
   */
  public getReceivedDateOptions(purchaseOrderReferences: string[]): Observable<ApiResponse<string[]>> {
    return this.httpClient.post<ApiResponse<string[]>>(`${this.baseUrl}/received-date-options`, purchaseOrderReferences);
  }

  /**
   * The day the shipment carrying this Order Invoice was received (#5165) — the date a pre-invoice is dated
   * by, and the same value the API stores on save.
   *
   * Deliberately NOT getReceivedDateOptions: a pre-invoice takes its date from its shipment, not from its
   * purchase orders' Goods Receipt Notes. An imported shipment is received through the shipment/DUCA flow
   * and its POs commonly have no GRNs at all, so the PO lookup answers nothing for exactly these documents.
   *
   * Null is a legitimate answer: the shipment has not been received yet.
   */
  public getShipmentReceivedDate(orderInvoiceReference: string): Observable<ApiResponse<string | null>> {
    return this.httpClient.get<ApiResponse<string | null>>(`${this.baseUrl}/received-date/by-order-invoice/${orderInvoiceReference}`);
  }

  /**
   * Which ItemClassification codes count as services (#4842), so the form can warn that a certificate is
   * coming while the clerk is still picking classifications. Fetched rather than hard-coded here on purpose:
   * the rule lives in the API, and a second copy in the UI would eventually disagree with it.
   */
  public getWithholdingClassificationRules(): Observable<ApiResponse<WithholdingClassificationRules>> {
    return this.httpClient.get<ApiResponse<WithholdingClassificationRules>>(`${this.baseUrl}/withholding-classification-rules`);
  }

  public search(
    command: ApDocumentSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
    matchAny = false,
  ): Observable<ApiResponse<PagedResult<ApDocument>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    if (matchAny) params = params.set('matchAny', true);
    return this.httpClient.post<ApiResponse<PagedResult<ApDocument>>>(`${this.baseUrl}/search`, command, { params });
  }

  /** The provider's payable invoices (Posted/PartiallyPaid with balance), optionally narrowed to a currency — for AP payment allocation. */
  public getPayableByProvider(providerReference: string, currency?: Currency | null): Observable<ApiResponse<ApDocument[]>> {
    let params = new HttpParams().set('providerReference', providerReference);
    if (currency != null) params = params.set('currency', currency);
    return this.httpClient.get<ApiResponse<ApDocument[]>>(`${this.baseUrl}/payable`, { params });
  }

  /** The provider's payables, each bundled with its Credit/Debit Notes + withholding + computed net payable
   * (#4514) — one batched call so the payment pickers show the true net without N+1. */
  public getPayableAggregated(providerReference: string, currency?: Currency | null): Observable<ApiResponse<PayableDocument[]>> {
    let params = new HttpParams().set('providerReference', providerReference);
    if (currency != null) params = params.set('currency', currency);
    return this.httpClient.get<ApiResponse<PayableDocument[]>>(`${this.baseUrl}/payable-aggregated`, { params });
  }

  /** The full payable picture for ONE document (#4516): its Credit/Debit Notes + withholding + net payable.
   * Unlike payable-aggregated this is not balance-scoped, so a fully-paid invoice still resolves — for the
   * resumen on a document's own detail page. */
  public getPayableDetail(id: string): Observable<ApiResponse<PayableDocument>> {
    return this.httpClient.get<ApiResponse<PayableDocument>>(`${this.baseUrl}/${id}/payable-detail`);
  }

  /** #4724 — per-PO-item ordered-vs-already-invoiced rollup for a PO. `excludeDocumentId` drops the
   * document being edited so the caller sees what was invoiced on OTHER documents. */
  public getPoInvoicedSummary(
    purchaseOrderReference: string,
    excludeDocumentId?: string | null,
  ): Observable<ApiResponse<PoItemInvoiced[]>> {
    let params = new HttpParams().set('purchaseOrderReference', purchaseOrderReference);
    if (excludeDocumentId) params = params.set('excludeDocumentId', excludeDocumentId);
    return this.httpClient.get<ApiResponse<PoItemInvoiced[]>>(`${this.baseUrl}/po-invoiced-summary`, { params });
  }

  public getSummary(): Observable<ApiResponse<ApDocumentSummary>> {
    // Company scope is resolved server-side from the current site.
    return this.httpClient.get<ApiResponse<ApDocumentSummary>>(`${this.baseUrl}/summary`);
  }

  /** Read-only preview of the next self-issued Purchase Receipt correlativo (#4465) — the draw happens on Post. */
  /** #6324 — the causes a Credit/Debit Note may carry for its reason; the API refuses any other. */
  public getNoteCauses(documentType: ApDocumentType, reason: ApNoteReason): Observable<ApiResponse<ApNoteCause[]>> {
    const params = new HttpParams().set('documentType', documentType).set('reason', reason);
    return this.httpClient.get<ApiResponse<ApNoteCause[]>>(`${this.baseUrl}/note-causes`, { params });
  }

  public getNextFiscalNumber(
    documentType: ApDocumentType = ApDocumentType.PurchaseReceipt,
  ): Observable<ApiResponse<PurchaseReceiptNextNumber>> {
    const params = new HttpParams().set('documentType', documentType);
    return this.httpClient.get<ApiResponse<PurchaseReceiptNextNumber>>(`${this.baseUrl}/next-fiscal-number`, { params });
  }

  /**
   * Deletes a DRAFT document (#5223). The API refuses anything further along, so this is safe to offer
   * from a draft screen and safe to have refused if the status moved on in another tab.
   */
  public delete(id: string): Observable<ApiResponse<string>> {
    return this.httpClient.delete<ApiResponse<string>>(`${this.baseUrl}/${id}`);
  }
}
